import type { SseFrame } from "@/shared/builder-session";
import { persistBackgroundFrame } from "@/features/chat/multi/transport/background-stream";

type SessionPainter = (frame: SseFrame) => void;

/** Live BuilderChat painters keyed by session id. */
const painters = new Map<string, SessionPainter>();

/** Graph frames that arrived before a painter claimed the session. */
const pendingGraphFrames = new Map<string, SseFrame[]>();

const GRAPH_EVENTS = new Set([
  "node-added",
  "node-updated",
  "graph-relayout",
  "edge-added",
  "edges-sync",
  "complete",
]);

export function registerSessionPainter(
  sessionId: string | null | undefined,
  paint: SessionPainter,
): () => void {
  const id = sessionId?.trim();
  if (!id) return () => undefined;
  painters.set(id, paint);
  const pending = pendingGraphFrames.get(id);
  if (pending?.length) {
    pendingGraphFrames.delete(id);
    for (const frame of pending) {
      try {
        paint(frame);
      } catch (error) {
        console.error("[session-paint] Pending graph frame failed", error);
      }
    }
  }
  return () => {
    if (painters.get(id) === paint) painters.delete(id);
  };
}

/** Force-drop painter on unmount so background SSE cannot paint a dead tree. */
export function clearSessionPainter(sessionId: string | null | undefined) {
  const id = sessionId?.trim();
  if (id) painters.delete(id);
}

export function hasSessionPainter(sessionId: string | null | undefined): boolean {
  const id = sessionId?.trim();
  return Boolean(id && painters.has(id));
}

/**
 * Paint into the mounted chat when present; otherwise persist for later restore.
 * Graph frames are queued until a painter claims the session (hub can replay
 * before claimPainter on a brand-new sessionId).
 */
export function dispatchSessionFrame(
  sessionId: string | null | undefined,
  frame: SseFrame,
): "live" | "background" | "dropped" {
  const id = sessionId?.trim();
  if (!id) return "dropped";
  const paint = painters.get(id);
  if (paint) {
    paint(frame);
    return "live";
  }
  if (GRAPH_EVENTS.has(frame.event)) {
    const queue = pendingGraphFrames.get(id) ?? [];
    queue.push(frame);
    // Cap so a long background build cannot grow without bound.
    if (queue.length > 200) queue.splice(0, queue.length - 200);
    pendingGraphFrames.set(id, queue);
    return "background";
  }
  persistBackgroundFrame(id, frame);
  return "background";
}
